import { useEffect, useMemo, useRef, useState } from 'react'
import { toPng } from 'html-to-image'
import { AlertTriangle, Download, ExternalLink, Loader2, MapPin, Pencil, Pin } from 'lucide-react'
import QRCode from 'qrcode'
import { eventPlansApi } from '../api/eventPlans'
import { EVENT_LABELS } from './EventLabelBadge'
import { useLanguage } from '../hooks/useLanguage'
import type { SmartItineraryAdjustment, SmartItineraryEvent, SmartItineraryPreview, SmartItineraryReflowResponse } from '../types/eventPlan'

const WEEKDAYS_ZH = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const WEEKDAYS_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const MONTHS_EN = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const THEME_COLORS: Record<string, string> = {
  infrastructure: '#3b82f6', ai_crypto: '#8b5cf6', rwa: '#14b8a6', regulation_policy: '#f59e0b',
  markets_trading: '#22c55e', payments: '#22c55e', defi: '#06b6d4', bitcoin: '#f97316',
  networking: '#ec4899', party: '#eab308', investment_fundraising: '#6366f1', privacy_identity: '#64748b',
}
const GENERIC_PLACEHOLDER = makePlaceholder('#3155a6')

function makePlaceholder(color: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="320"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="${color}"/><stop offset="1" stop-color="#07152f"/></linearGradient></defs><rect width="480" height="320" fill="url(#g)"/><circle cx="400" cy="45" r="105" fill="#fff" opacity=".08"/><path d="M32 252h416M32 270h250" stroke="#fff" opacity=".2" stroke-width="4"/><text x="32" y="78" fill="#f8d35d" font-family="Arial" font-size="22" font-weight="700">TOKEN2049</text><text x="32" y="112" fill="white" font-family="Arial" font-size="15">SINGAPORE 2026</text></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

function eventImage(event: Pick<SmartItineraryEvent, 'image_url' | 'event_label'>) {
  return event.image_url || makePlaceholder(THEME_COLORS[event.event_label] || '#3155a6')
}

function dateParts(value: string, lang: 'zh' | 'en') {
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return {
    weekday: lang === 'zh' ? WEEKDAYS_ZH[date.getDay()] : WEEKDAYS_EN[date.getDay()],
    day: String(day),
    month: lang === 'zh' ? `${month}月` : MONTHS_EN[month - 1],
  }
}

function sourceBadge(sourceId: string, lang: 'zh' | 'en') {
  if (sourceId === 'token2049_agenda') return lang === 'zh' ? 'TOKEN2049 官方议程' : 'TOKEN2049 OFFICIAL'
  if (sourceId === 'token2049_week') return 'TOKEN2049 WEEK'
  return lang === 'zh' ? 'SIDE EVENT' : 'SIDE EVENT'
}

function linkHost(value?: string) {
  if (!value) return ''
  try { return new URL(value).hostname.replace(/^www\./, '') } catch { return '' }
}

interface Props {
  plan: SmartItineraryPreview
  lang: 'zh' | 'en'
  onModify: () => void
  onReflow: (result: SmartItineraryReflowResponse) => void
  onTogglePin: (eventId: string) => void
}

export function ItineraryPoster({ plan, lang, onModify, onReflow, onTogglePin }: Props) {
  const { t } = useLanguage()
  const posterRef = useRef<HTMLElement>(null)
  const adjustmentRequestRef = useRef(0)
  const [adjustingId, setAdjustingId] = useState<string | null>(null)
  const [adjustments, setAdjustments] = useState<SmartItineraryAdjustment[]>([])
  const [adjustmentLoading, setAdjustmentLoading] = useState(false)
  const [adjustmentError, setAdjustmentError] = useState('')
  const [visibleAdjustments, setVisibleAdjustments] = useState(8)
  const [reflowingId, setReflowingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [shareLoading, setShareLoading] = useState(true)
  const [shareError, setShareError] = useState('')
  const [shareLink, setShareLink] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [shareRetry, setShareRetry] = useState(0)
  const plannedEventIds = useMemo(() => plan.days.flatMap((day) => day.events.map((event) => event.id)), [plan])
  const pinnedEventIds = useMemo(() => new Set(plan.pinned_event_ids || []), [plan.pinned_event_ids])

  useEffect(() => {
    let cancelled = false
    setShareLoading(true)
    setShareError('')
    setShareLink('')
    setQrCode('')
    eventPlansApi.createShare(plan).then(async (shared) => {
      const image = await QRCode.toDataURL(shared.deep_link, {
        errorCorrectionLevel: 'M', margin: 4, width: 320,
        color: { dark: '#071228', light: '#ffffff' },
      })
      if (!cancelled) {
        setShareLink(shared.deep_link)
        setQrCode(image)
      }
    }).catch((reason) => {
      if (!cancelled) setShareError(reason instanceof Error ? reason.message : String(reason))
    }).finally(() => {
      if (!cancelled) setShareLoading(false)
    })
    return () => { cancelled = true }
  }, [plan, shareRetry])

  const relationText = (candidate: SmartItineraryAdjustment) => {
    if (candidate.time_relation === 'same') return t('相同开始时间', 'Same start time')
    const minutes = Math.abs(candidate.start_offset_minutes)
    const duration = minutes < 60
      ? t(`${minutes} 分钟`, `${minutes} min`)
      : minutes % 60 === 0
        ? `${minutes / 60} ${t('小时', 'hr')}`
        : t(`${Math.floor(minutes / 60)} 小时 ${minutes % 60} 分钟`, `${Math.floor(minutes / 60)} hr ${minutes % 60} min`)
    return candidate.time_relation === 'earlier'
      ? t(`提前 ${duration} 开始`, `Starts ${duration} earlier`)
      : t(`延后 ${duration} 开始`, `Starts ${duration} later`)
  }

  const openAdjustments = async (event: SmartItineraryEvent) => {
    if (pinnedEventIds.has(event.id)) return
    if (adjustingId === event.id) {
      adjustmentRequestRef.current += 1
      setAdjustingId(null)
      return
    }
    const requestId = adjustmentRequestRef.current + 1
    adjustmentRequestRef.current = requestId
    setAdjustingId(event.id)
    setAdjustments([])
    setVisibleAdjustments(8)
    setAdjustmentLoading(true)
    setAdjustmentError('')
    try {
      const result = await eventPlansApi.adjustments(
        plan.selection.event_types,
        event.date,
        plan.selection.buffer_minutes,
        plannedEventIds,
        event.id,
        [...pinnedEventIds],
      )
      if (adjustmentRequestRef.current === requestId) setAdjustments(result.candidates)
    } catch (reason) {
      if (adjustmentRequestRef.current === requestId) setAdjustmentError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      if (adjustmentRequestRef.current === requestId) setAdjustmentLoading(false)
    }
  }

  const chooseReplacement = async (target: SmartItineraryEvent, replacement: SmartItineraryAdjustment) => {
    if (reflowingId || replacement.blocked_by_pinned_event_ids.length) return
    setReflowingId(replacement.id)
    setAdjustmentError('')
    try {
      const result = await eventPlansApi.reflow(
        plan.selection.event_types,
        target.date,
        plan.selection.buffer_minutes,
        plannedEventIds,
        target.id,
        replacement.id,
        [...pinnedEventIds],
      )
      onReflow(result)
      setAdjustingId(null)
      setAdjustments([])
    } catch (reason) {
      setAdjustmentError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setReflowingId(null)
    }
  }

  const togglePin = (eventId: string) => {
    if (reflowingId) return
    if (!pinnedEventIds.has(eventId) && adjustingId === eventId) {
      adjustmentRequestRef.current += 1
      setAdjustingId(null)
      setAdjustments([])
      setAdjustmentError('')
    }
    onTogglePin(eventId)
  }

  const savePoster = async () => {
    const node = posterRef.current
    if (!node || saving) return
    setSaving(true)
    setSaveError('')
    try {
      await document.fonts.ready
      const pixelRatio = Math.max(2, 1080 / Math.max(node.scrollWidth, 1))
      const dataUrl = await toPng(node, {
        backgroundColor: '#071228',
        cacheBust: true,
        imagePlaceholder: GENERIC_PLACEHOLDER,
        pixelRatio,
        filter: (element) => !(element instanceof HTMLElement && element.dataset.posterControl === 'true'),
      })
      const response = await fetch(dataUrl)
      const file = new File([await response.blob()], 'token2049-itinerary.png', { type: 'image/png' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'TOKEN2049 Itinerary' })
      } else {
        const anchor = document.createElement('a')
        anchor.download = file.name
        anchor.href = dataUrl
        anchor.click()
      }
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return
      setSaveError(t('图片保存失败，请稍后重试', 'Could not save the image. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  return <section className="space-y-3">
    <div className="flex items-center justify-between gap-3">
      <div>
        <h2 className="text-base font-bold text-gray-950 dark:text-white">{t('你的行程方案', 'Your itinerary')}</h2>
        <p className="mt-1 text-xs text-gray-500">{t(`${plan.candidate_count} 场待选 · 已安排 ${plan.selected_count} 场`, `${plan.candidate_count} candidates · ${plan.selected_count} selected`)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" onClick={onModify} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"><Pencil className="h-3.5 w-3.5" />{t('修改条件', 'Edit')}</button>
        <button type="button" onClick={() => void savePoster()} disabled={saving || shareLoading || !qrCode} className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 shadow-sm disabled:cursor-not-allowed disabled:opacity-50">{saving || shareLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}{shareLoading ? t('生成二维码', 'Creating QR') : t('保存图片', 'Save image')}</button>
      </div>
    </div>
    {saveError && <p className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{saveError}</p>}
    {shareError && <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700"><span>{shareError}</span><button type="button" onClick={() => setShareRetry((value) => value + 1)} className="shrink-0 font-bold">{t('重试', 'Retry')}</button></div>}

    <section ref={posterRef} className="overflow-hidden rounded-[28px] bg-[#071228] text-white shadow-2xl shadow-blue-950/20">
      <header className="relative overflow-hidden border-b border-white/10 px-5 py-6">
        <div className="absolute -right-12 -top-20 h-52 w-52 rounded-full bg-blue-500/20 blur-2xl" />
        <div className="absolute -left-10 bottom-0 h-24 w-44 rounded-full bg-amber-400/10 blur-2xl" />
        <p className="relative text-[10px] font-bold uppercase tracking-[0.3em] text-amber-300">Singapore · 04–11 OCT 2026</p>
        <h3 className="relative mt-2 text-2xl font-black tracking-tight">YOUR TOKEN2049<br />ITINERARY</h3>
        <p className="relative mt-3 text-xs text-blue-100/70">{t(`${plan.selected_count} 场专属活动 · 按时间顺序排列`, `${plan.selected_count} selected events · In chronological order`)}</p>
      </header>

      <div className="space-y-4 p-3.5 sm:p-5">
        {plan.days.map((day) => {
          return <section key={day.date} className="space-y-2.5">
          {day.events.length ? day.events.map((event) => {
            const date = dateParts(event.date, lang)
            const label = EVENT_LABELS[event.event_label]?.[lang] || event.event_label
            const isPinned = pinnedEventIds.has(event.id)
            const accent = THEME_COLORS[event.event_label] || '#3155a6'
            return <article key={event.id} className="overflow-hidden rounded-2xl border border-white/10 bg-[#0d1b35] shadow-lg shadow-black/10">
              <div className="h-1" style={{ backgroundColor: accent }} />
              <div className="px-3 pb-3 pt-2.5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5"><span className="rounded-full bg-amber-300 px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-[#071228]">{sourceBadge(event.source_id, lang)}</span></div>
                  <div className="flex shrink-0 items-center gap-1.5"><span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] font-bold text-blue-100">{label}</span><button data-poster-control="true" type="button" disabled={Boolean(reflowingId)} onClick={() => togglePin(event.id)} aria-pressed={isPinned} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold transition disabled:cursor-wait disabled:opacity-50 ${isPinned ? 'border-amber-300 bg-amber-300 text-[#071228]' : 'border-white/15 bg-white/5 text-blue-100 hover:border-amber-300/60 hover:text-amber-200'}`}><Pin className="h-2.5 w-2.5" />{isPinned ? t('已固定', 'Pinned') : t('固定', 'Pin')}</button></div>
                </div>
                <div className="grid grid-cols-[42px_48px_minmax(0,1fr)] gap-2.5 sm:grid-cols-[48px_58px_minmax(0,1fr)] sm:gap-3">
                  <div className="border-r border-white/10 pr-2 text-center">
                    <div className="text-[9px] font-bold text-blue-200">{date.weekday}</div>
                    <div className="my-0.5 text-2xl font-black leading-none text-white">{date.day}</div>
                    <div className="text-[9px] font-bold text-amber-300">{date.month}</div>
                  </div>
                  <div className="flex flex-col items-center justify-center text-center text-[10px] font-bold leading-4 text-white">
                    <span>{event.start_time}</span><span className="my-0.5 h-px w-4 bg-blue-300/40" /><span>{event.end_time}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-start gap-2.5">
                      <img src={eventImage(event)} crossOrigin="anonymous" alt="" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = makePlaceholder(accent) }} className="h-16 w-16 shrink-0 rounded-xl object-cover sm:h-20 sm:w-24" />
                      <div className="min-w-0 flex-1">
                        <h4 className="break-words text-[13px] font-extrabold leading-[1.3] text-white sm:text-sm">{event.title}</h4>
                        {(event.category || event.price) && <p className="mt-1 text-[10px] font-semibold leading-4 text-blue-100/70">{[event.category, event.price].filter(Boolean).join(' · ')}</p>}
                      </div>
                    </div>
                    {event.location && <p className="mt-2 flex items-start gap-1 text-[10px] leading-4 text-blue-100/65"><MapPin className="mt-0.5 h-3 w-3 shrink-0 text-amber-300" /><span className="break-words">{event.location}</span></p>}
                    {event.time_notice && <p className="mt-2 flex items-start gap-1 rounded-lg bg-amber-300/10 px-2 py-1.5 text-[9px] leading-4 text-amber-200"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{event.time_notice}</p>}
                    <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-white/10 pt-2.5">
                      <button data-poster-control="true" type="button" disabled={isPinned} onClick={() => void openAdjustments(event)} className="text-[10px] font-bold text-blue-200 hover:text-white disabled:cursor-not-allowed disabled:text-blue-100/35">{isPinned ? t('已固定', 'Pinned') : t('调整活动', 'Adjust event')}</button>
                      {event.detail_url ? <a href={event.detail_url} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-1 text-[10px] font-bold text-amber-300"><span className="max-w-[95px] truncate">{linkHost(event.detail_url)}</span><span>{t('详情', 'DETAILS')}</span><ExternalLink className="h-3 w-3 shrink-0" /></a> : <span className="text-[9px] font-semibold text-blue-100/35">{t('无详情链接', 'NO DETAILS')}</span>}
                    </div>
                  </div>
                </div>
              </div>
              {adjustingId === event.id && <div data-poster-control="true" className="border-t border-white/10 bg-[#08162e] px-3 py-3">
                <p className="mb-2 text-[10px] leading-4 text-blue-100/55">{t('当天已选主题中的全部可替换活动；选择后会保留不冲突活动并自动补位', 'All same-day events from your selected topics; safe events stay and open gaps are refilled')}</p>
                {adjustmentLoading ? <div className="flex justify-center py-5"><Loader2 className="h-5 w-5 animate-spin text-blue-300" /></div> : adjustmentError ? <div className="rounded-lg bg-red-950/40 px-3 py-2 text-xs text-red-200">{adjustmentError}</div> : adjustments.length ? <div className="space-y-2">{adjustments.slice(0, visibleAdjustments).map((candidate) => {
                  const blocked = candidate.blocked_by_pinned_event_ids.length > 0
                  return <button key={candidate.id} type="button" disabled={Boolean(reflowingId) || blocked} onClick={() => void chooseReplacement(event, candidate)} className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-left hover:border-blue-300/40 disabled:cursor-not-allowed disabled:opacity-45"><div className="flex items-start justify-between gap-2"><span className="shrink-0 text-xs font-bold text-blue-200">{candidate.start_time}–{candidate.end_time}</span><div className="flex flex-wrap items-center justify-end gap-1.5"><span className="rounded-full bg-amber-300/10 px-2 py-0.5 text-[9px] font-semibold text-amber-200">{relationText(candidate)}</span>{blocked && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-blue-100/60">{t('与固定活动冲突', 'Conflicts with pinned event')}</span>}{reflowingId === candidate.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-200" />}</div></div><span className="mt-1 block text-xs font-semibold text-white">{candidate.title}</span>{candidate.location && <span className="mt-1 block truncate text-[10px] text-blue-100/55">{candidate.location}</span>}</button>
                })}{visibleAdjustments < adjustments.length && <button type="button" onClick={() => setVisibleAdjustments((count) => count + 8)} className="w-full py-2 text-xs font-bold text-blue-200">{t(`查看更多（剩余 ${adjustments.length - visibleAdjustments}）`, `Show more (${adjustments.length - visibleAdjustments} remaining)`)}</button>}</div> : <div className="rounded-lg border border-dashed border-white/10 py-5 text-center text-xs text-blue-100/45">{t('当天暂无其他可替换活动', 'No other same-day replacement events')}</div>}
              </div>}
            </article>
          }) : <div className="rounded-2xl border border-dashed border-white/10 py-6 text-center text-xs text-blue-100/45">{t('当天没有可安排的活动', 'No schedulable events')}</div>}
        </section>})}
      </div>
      <div className="border-t border-white/10 px-5 py-5">
        <div className="flex items-center gap-4 rounded-2xl bg-white p-3 text-[#071228]">
          {qrCode && shareLink ? <a href={shareLink} target="_blank" rel="noreferrer" className="shrink-0"><img src={qrCode} alt={t('共享行程二维码', 'Shared itinerary QR code')} className="h-28 w-28 rounded-lg" /></a> : <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-lg bg-gray-100"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>}
          <div className="min-w-0">
            <p className="text-sm font-black leading-5">{t('扫码导入此行程', 'Scan to import this itinerary')}</p>
            <p className="mt-1.5 text-[10px] leading-4 text-slate-500">{t('使用 Telegram 扫码，预览后保存到你的专属行程。', 'Scan with Telegram, preview it, then save it as your itinerary.')}</p>
            <p className="mt-2 text-[9px] font-bold uppercase tracking-wider text-blue-700">Telegram Mini App</p>
          </div>
        </div>
      </div>
      <footer className="border-t border-white/10 px-5 py-4 text-center text-[9px] font-bold uppercase tracking-[0.24em] text-blue-100/40">Discover · Plan · Experience TOKEN2049</footer>
    </section>
  </section>
}
